import React from "react";

// R1b-δ.2 a11y sweep — text/border tuned so each chip clears WCAG AA (>= 4.5:1)
// against its own background, including the previously-borderline `phase_n`
// tile chip on cream backgrounds (was #78716C → 4.2:1; now #4A4A47 → 8.5:1).
const PALETTE = {
  not_configured: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Not Configured" },
  connecting: { bg: "#EFF6FF", text: "#1E3A8A", border: "#BFDBFE", label: "Connecting…" },
  // Stage A · Chip Correction — canonical six-way display states.
  // Backend `_derive_display_state` is the single source of truth; every
  // display_state value it emits MUST have a palette entry here.
  authorization_required: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", label: "Authorization Required" },
  configured_disconnected: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Configured but Disconnected" },
  connected: { bg: "#ECFDF5", text: "#065F46", border: "#A7F3D0", label: "Connected" },
  // Env-union read — adapter is configured via pod-level platform secret
  // (Manage → Secrets write-through to `.env`). Workers consume the env
  // value as fallback when no vault credential is set. Chip tone is a
  // calm teal, distinct from `connected`'s green but similarly positive.
  // Palette audited WCAG AA: text #0E5C64 on bg #ECFEFF → 7.9:1 contrast.
  configured_via_env: { bg: "#ECFEFF", text: "#0E5C64", border: "#A5F3FC", label: "Configured (platform secret)" },
  degraded: { bg: "#FFF7ED", text: "#9A3412", border: "#FFEDD5", label: "Degraded" },
  error: { bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", label: "Error" },
  rate_limited: { bg: "#FEF3C7", text: "#78350F", border: "#FDE68A", label: "Rate Limited" },
  // R1c retired `demonstration` — any legacy value falls back to Not Configured.
  demonstration: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Not Configured" },
  // B2a — no roadmap wording: a built module that is off for this workspace is "Available to Activate".
  phase_n: { bg: "#F5F5F4", text: "#4A4A47", border: "#D6D3D1", label: "Available to Activate" },
  // B2a module states (backend services/module_register.py) + integration truth states.
  active: { bg: "#ECFDF5", text: "#065F46", border: "#A7F3D0", label: "Active" },
  available_to_activate: { bg: "#F5F5F4", text: "#4A4A47", border: "#D6D3D1", label: "Available to Activate" },
  not_implemented: { bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA", label: "Not Implemented" },
  externally_blocked: { bg: "#FEF2F2", text: "#7F1D1D", border: "#FCA5A5", label: "Externally Blocked" },
  connected_verified: { bg: "#ECFDF5", text: "#065F46", border: "#6EE7B7", label: "Connected and Verified" },
  configured_not_authorized: { bg: "#FEF3C7", text: "#78350F", border: "#FDE68A", label: "Configured but not authorized" },
  disconnected: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Disconnected" },
  verified: { bg: "#ECFDF5", text: "#065F46", border: "#6EE7B7", label: "Verified" },
  pending: { bg: "#FEF3C7", text: "#78350F", border: "#FCD34D", label: "Pending" },
  expired: { bg: "#FEF2F2", text: "#7F1D1D", border: "#FCA5A5", label: "Expired" },
  approved: { bg: "#ECFDF5", text: "#065F46", border: "#6EE7B7", label: "Approved" },
  rejected: { bg: "#FEF2F2", text: "#7F1D1D", border: "#FCA5A5", label: "Rejected" },
  pending_approval: { bg: "#EEF2FF", text: "#312E81", border: "#C7D2FE", label: "Pending Approval" },
  draft: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Draft" },
  in_review: { bg: "#EEF2FF", text: "#312E81", border: "#C7D2FE", label: "In Review" },
  published: { bg: "#ECFDF5", text: "#065F46", border: "#6EE7B7", label: "Published" },
  unpublished: { bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA", label: "Unpublished" },
  archived: { bg: "#F5F5F4", text: "#44403C", border: "#D6D3D1", label: "Archived" },
  uploaded: { bg: "#EFF6FF", text: "#1E3A8A", border: "#BFDBFE", label: "Uploaded" },
  processing: { bg: "#EFF6FF", text: "#1E3A8A", border: "#BFDBFE", label: "Processing" },
  ready: { bg: "#ECFDF5", text: "#065F46", border: "#A7F3D0", label: "Ready" },
  failed: { bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", label: "Failed" },
  test: { bg: "#FEE2E2", text: "#7F1D1D", border: "#FCA5A5", label: "TEST ACCOUNT" },
};

// States whose label must read exactly as written (sentence case), never CSS-uppercased.
const AS_WRITTEN = new Set(["configured_not_authorized"]);

export default function StatusChip({ status = "not_configured", label, phase, className = "", testId }) {
  // Stage A · defense-in-depth — surface unmapped statuses honestly instead
  // of silently falling back to "Not Configured". Unknown values render with
  // the neutral not_configured palette but the LABEL preserves the raw
  // string (title-cased) so the failure mode is visible, not fabricated.
  const known = Boolean(PALETTE[status]);
  const meta = known ? PALETTE[status] : PALETTE.not_configured;
  let text = label ?? meta.label;
  if (!known && !label) {
    text = String(status || "unknown")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-nav font-semibold ${AS_WRITTEN.has(status) ? "" : "uppercase tracking-wider "}border ${className}`}
      style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}
      data-testid={testId || `status-chip-${status}`}
      role="status"
      aria-label={text}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: meta.text }} aria-hidden="true" />
      {text}
    </span>
  );
}
